import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Stack,
} from '@mui/material';
import {
  Description,
  Psychology,
  TrendingUp,
  AutoAwesome,
  WorkspacePremium,
  Timeline,
} from '@mui/icons-material';

const features = [
  {
    icon: Psychology,
    title: 'Career Story Analysis',
    description: 'Get a \u003cb\u003ecomprehensive analysis of your unique career journey\u003c/b\u003e. Discover the patterns, strengths, and distinctive value that make your professional story extraordinary.',
    color: 'var(--color-brand-primary)',
    gradient: 'linear-gradient(135deg, var(--color-brand-primary), var(--color-brand-dark))',
  },
  {
    icon: WorkspacePremium,
    title: 'Log Your Wins',
    description: 'Capture accomplishments effortlessly by \u003cb\u003etyping or speaking\u003c/b\u003e. Kazi automatically organizes them by role and time period with \u003cb\u003eAI-generated summaries\u003c/b\u003e.',
    color: 'var(--color-accent-primary)',
    gradient: 'linear-gradient(135deg, var(--color-accent-primary), var(--color-accent-dark))',
  },
  {
    icon: Timeline,
    title: 'Job Match Analysis',
    description: 'Paste any job posting and see your \u003cb\u003epercentage match score\u003c/b\u003e. Get detailed insights on \u003cb\u003estrengths to highlight\u003c/b\u003e and gaps to address.',
    color: 'var(--color-brand-light)',
    gradient: 'linear-gradient(135deg, var(--color-brand-light), var(--color-brand-primary))',
  },
  {
    icon: Description,
    title: 'Smart Resume Generation',
    description: 'Generate \u003cb\u003eAI-enhanced resumes\u003c/b\u003e tailored to specific roles. Request variations like "shorter," "more detailed," or "optimized for leadership" with a single prompt.',
    color: 'var(--color-brand-primary)',
    gradient: 'linear-gradient(135deg, var(--color-brand-dark), var(--color-brand-primary))',
  },
  {
    icon: AutoAwesome,
    title: 'Cover Letter Creation',
    description: 'Generate \u003cb\u003ejob-specific cover letters\u003c/b\u003e that speak directly to each opportunity. Edit and refine until they perfectly represent your voice and value.',
    color: 'var(--color-accent-primary)',
    gradient: 'linear-gradient(135deg, var(--color-accent-dark), var(--color-accent-primary))',
  },
  {
    icon: TrendingUp,
    title: 'Career Pivot Discovery',
    description: 'Explore \u003cb\u003erealistic career transition opportunities\u003c/b\u003e based on your skills. Dive into \u003cb\u003eBLS labor market data\u003c/b\u003e for salary, outlook, and role insights.',
    color: 'var(--color-brand-light)',
    gradient: 'linear-gradient(135deg, var(--color-brand-primary), var(--color-brand-light))',
  },
];

function Features() {
  const [mounted, setMounted] = useState(false);
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  useEffect(() => {
    setMounted(true);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.3,
      },
    },
  };

  const headerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const iconVariants = {
    hidden: { scale: 0, rotate: -180 },
    visible: {
      scale: 1,
      rotate: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  if (!mounted) return null;

  return (
    <Box className="section-padding parallax-container" sx={{ backgroundColor: 'var(--color-neutral-50)', position: 'relative' }}>
      <div className="parallax-bg" />

      <Container maxWidth="lg">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={containerVariants}
        >
          <Stack spacing={8} alignItems="center">
            <motion.div variants={headerVariants}>
              <Box textAlign="center" sx={{ maxWidth: '700px' }}>
                <Typography
                  variant="overline"
                  sx={{
                    color: 'var(--color-brand-primary)',
                    fontWeight: 700,
                    letterSpacing: 2,
                    fontSize: '0.875rem',
                    mb: 2,
                    display: 'block',
                  }}
                >
                  POWERFUL FEATURES
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    background: 'linear-gradient(135deg, var(--color-brand-primary), var(--color-brand-dark), var(--color-accent-primary))',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundSize: '200% 200%',
                    animation: 'gradientShift 6s ease infinite',
                    mb: 3,
                    fontWeight: 700,
                  }}
                >
                  Your Comprehensive Career Intelligence Platform
                </Typography>
                {/* <Typography
                  variant="body1"
                  sx={{
                    color: 'var(--color-neutral-600)',
                    fontSize: '1.25rem',
                    lineHeight: 1.7,
                  }}
                >
                  A complete toolkit for understanding your unique value, tracking your growth, and presenting your best self to every opportunity.
                </Typography> */}
              </Box>
            </motion.div>

            <Grid container spacing={4} alignItems="stretch">
              {features.map((feature, index) => (
                <Grid item xs={12} md={6} lg={4} key={index} sx={{ display: 'flex' }}>
                  <motion.div
                    variants={cardVariants}
                    whileHover={{
                      y: -8,
                      scale: 1.02,
                      transition: { duration: 0.3 }
                    }}
                    style={{ width: '100%', height: '100%' }}
                  >
                    <Card
                      component={motion.div}
                      sx={{
                        height: '100%',
                        border: '1px solid rgba(255, 49, 48, 0.1)',
                        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.7))',
                        backdropFilter: 'blur(20px)',
                        borderRadius: 4,
                        overflow: 'hidden',
                        position: 'relative',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          boxShadow: '0 20px 40px rgba(255, 49, 48, 0.15)',
                          border: '1px solid rgba(255, 49, 48, 0.2)',
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: 4,
                          background: feature.gradient,
                        },
                      }}
                    >
                      <CardContent sx={{ p: 4, position: 'relative' }}>
                        <Stack spacing={3}>
                          <motion.div
                            variants={iconVariants}
                            whileHover={{
                              scale: 1.1,
                              rotate: 5,
                              transition: { duration: 0.3 }
                            }}
                          >
                            <Box
                              sx={{
                                width: 64,
                                height: 64,
                                borderRadius: 3,
                                background: feature.gradient,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                                boxShadow: `0 8px 25px ${feature.color}40`,
                                '&::after': {
                                  content: '""',
                                  position: 'absolute',
                                  top: -2,
                                  left: -2,
                                  right: -2,
                                  bottom: -2,
                                  background: feature.gradient,
                                  borderRadius: 3,
                                  opacity: 0,
                                  transition: 'opacity 0.3s',
                                  zIndex: -1,
                                },
                                '&:hover::after': {
                                  opacity: 0.5,
                                },
                              }}
                            >
                              <feature.icon
                                sx={{
                                  fontSize: 32,
                                  color: 'white',
                                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
                                }}
                              />
                            </Box>
                          </motion.div>

                          <Typography
                            variant="h4"
                            sx={{
                              color: 'var(--color-neutral-900)',
                              fontSize: '1.5rem',
                              fontWeight: 700,
                              mb: 1,
                            }}
                          >
                            {feature.title}
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{
                              color: 'var(--color-neutral-600)',
                              lineHeight: 1.7,
                              fontSize: '1rem',
                              '& b': {
                                color: 'var(--color-brand-primary)',
                                fontWeight: 600,
                              },
                            }}
                            dangerouslySetInnerHTML={{ __html: feature.description }}
                          />
                        </Stack>

                        {/* Floating Decoration */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 20,
                            right: 20,
                            width: 30,
                            height: 30,
                            borderRadius: '50%',
                            background: `${feature.color}20`,
                            opacity: 0,
                            transition: 'all 0.4s ease',
                            '.MuiCard-root:hover &': {
                              opacity: 1,
                              transform: 'scale(1.2)',
                            },
                          }}
                        />

                        {/* Gradient Overlay */}
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: '40%',
                            background: `linear-gradient(to top, ${feature.color}05, transparent)`,
                            pointerEvents: 'none',
                            opacity: 0,
                            transition: 'opacity 0.4s ease',
                            '.MuiCard-root:hover &': {
                              opacity: 1,
                            },
                          }}
                        />
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Stack>
        </motion.div>
      </Container>
    </Box>
  );
}

export default Features; 